import React, { useEffect, useState, useCallback } from "react";
import { Bell } from "lucide-react";
import api from "../lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Button } from "./ui/button";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - then);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

function tierLabel(pkg) {
  if (pkg === "virtual_full") return "Full";
  if (pkg === "virtual_concession") return "Concession";
  return "Ticket";
}

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(0);
  const [items, setItems] = useState([]);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/notifications", { params: { limit: 30 } });
      setUnread(data.unread || 0);
      setItems(data.notifications || []);
    } catch {
      // silent — bell is non-essential
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, [load]);

  const markAllRead = async () => {
    try {
      await api.post("/admin/notifications/mark-read", { all: true });
      setUnread(0);
      setItems((arr) => arr.map((n) => ({ ...n, is_read: true })));
    } catch {
      // ignore
    }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) load(); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="relative inline-flex items-center justify-center h-9 w-9 rounded-full hover:bg-slate-100 text-slate-700"
          aria-label="Notifications"
          data-testid="admin-notifications-bell"
        >
          <Bell size={17} />
          {unread > 0 && (
            <span
              className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-semibold flex items-center justify-center ring-2 ring-white"
              data-testid="admin-notifications-unread"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-[360px] p-0 rounded-2xl overflow-hidden"
        data-testid="admin-notifications-popover"
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <div className="text-xs uppercase tracking-[0.22em] text-slate-500 font-semibold">Activity</div>
          {unread > 0 && (
            <Button
              variant="ghost"
              size="sm"
              className="text-xs text-[#28BBE3] hover:text-[#1FA8CC] hover:bg-transparent h-auto px-2 py-1"
              onClick={markAllRead}
              data-testid="admin-notifications-mark-all-read"
            >
              Mark all read
            </Button>
          )}
        </div>
        <div className="max-h-[420px] overflow-y-auto">
          {items.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-slate-400">
              No activity yet.
            </div>
          ) : (
            items.map((n) => {
              const p = n.payload || {};
              const isPurchase = n.type === "purchase";
              return (
                <div
                  key={n.notification_id}
                  className={`flex gap-3 px-4 py-3 border-b border-slate-50 ${
                    n.is_read ? "bg-white" : "bg-sky-50/40"
                  }`}
                  data-testid={`notification-${n.notification_id}`}
                >
                  <div className="shrink-0 h-2 w-2 mt-2 rounded-full bg-[#28BBE3] aria-hidden">
                    {n.is_read && <span className="block h-2 w-2 rounded-full bg-slate-300" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    {isPurchase ? (
                      <>
                        <div className="text-sm text-slate-900">
                          <span className="font-semibold">New {tierLabel(p.package_id)} sale</span>
                          {p.amount_gbp > 0 && (
                            <span className="ml-1 text-slate-600">· £{Number(p.amount_gbp).toFixed(2)}</span>
                          )}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-600 truncate">
                          {p.name || p.email}
                        </div>
                        {p.name && p.email && (
                          <div className="text-xs text-slate-400 truncate">{p.email}</div>
                        )}
                      </>
                    ) : n.type === "support" ? (
                      <>
                        <div className="text-sm text-slate-900">
                          <span className="font-semibold">Support ticket</span>
                          {p.category && <span className="ml-1 text-slate-600">· {p.category}</span>}
                        </div>
                        <div className="mt-0.5 text-xs text-slate-600 truncate">{p.name || p.email}</div>
                        {p.message_preview && (
                          <div className="mt-0.5 text-xs text-slate-500 line-clamp-2">{p.message_preview}</div>
                        )}
                      </>
                    ) : (
                      <div className="text-sm text-slate-900">{n.type}</div>
                    )}
                    <div className="mt-1 text-[11px] text-slate-400">{timeAgo(n.created_at)}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
